/**
 * Audio Overview Block - Custom Audio Player
 * Handles play/pause, progress bar seeking, and time display
 */

(function() {
  'use strict';

  // Store all audio player instances
  const audioPlayers = [];

  /**
   * Format time in MM:SS format
   * @param {number} seconds - Time in seconds
   * @returns {string} Formatted time string
   */
  function formatTime(seconds) {
    if (isNaN(seconds) || !isFinite(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Initialize a single audio player
   * @param {HTMLElement} container - The audio overview container element
   */
  function initAudioPlayer(container) {
    const audio = container.querySelector('.ao-audio');
    const playBtn = container.querySelector('.ao-play-btn');
    const progressBar = container.querySelector('.ao-progress-bar');
    const progressFill = container.querySelector('.ao-progress-fill');
    const progressThumb = container.querySelector('.ao-progress-thumb');
    const currentTimeEl = container.querySelector('.ao-current');
    const durationEl = container.querySelector('.ao-duration');

    if (!audio || !playBtn || !progressBar || !progressFill) {
      return;
    }

    // Load audio source from data attribute
    const audioSrc = container.dataset.audioSrc;
    if (audioSrc && !audio.src) {
      audio.src = audioSrc;
      audio.load();
    }

    // Update duration when metadata loads
    audio.addEventListener('loadedmetadata', function() {
      durationEl.textContent = formatTime(audio.duration);
    });

    // Handle duration display if metadata already loaded
    if (audio.duration && !isNaN(audio.duration)) {
      durationEl.textContent = formatTime(audio.duration);
    }

    // Play/Pause toggle
    playBtn.addEventListener('click', function() {
      if (audio.paused) {
        // Pause all other audio players first
        pauseAllExcept(container);
        audio.play();
      } else {
        audio.pause();
      }
    });

    // Update UI when playing
    audio.addEventListener('play', function() {
      container.classList.add('is-playing');
      playBtn.setAttribute('aria-label', 'Pause audio');
    });

    // Update UI when paused
    audio.addEventListener('pause', function() {
      container.classList.remove('is-playing');
      playBtn.setAttribute('aria-label', 'Play audio');
    });

    // Update progress bar, thumb, and current time during playback
    audio.addEventListener('timeupdate', function() {
      const progress = (audio.currentTime / audio.duration) * 100;
      progressFill.style.width = `${progress}%`;
      if (progressThumb) {
        progressThumb.style.left = `${progress}%`;
      }
      currentTimeEl.textContent = formatTime(audio.currentTime);
    });

    // Reset when audio ends
    audio.addEventListener('ended', function() {
      container.classList.remove('is-playing');
      playBtn.setAttribute('aria-label', 'Play audio');
      progressFill.style.width = '0%';
      if (progressThumb) {
        progressThumb.style.left = '0%';
      }
      currentTimeEl.textContent = '0:00';
    });

    // Click to seek on progress bar
    progressBar.addEventListener('click', function(e) {
      const rect = progressBar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = clickX / width;
      const newTime = percentage * audio.duration;

      if (!isNaN(newTime) && isFinite(newTime)) {
        audio.currentTime = newTime;
      }
    });

    // Make progress bar accessible via keyboard
    progressBar.setAttribute('tabindex', '0');
    progressBar.setAttribute('role', 'slider');
    progressBar.setAttribute('aria-label', 'Audio progress');
    progressBar.setAttribute('aria-valuemin', '0');
    progressBar.setAttribute('aria-valuemax', '100');
    progressBar.setAttribute('aria-valuenow', '0');

    progressBar.addEventListener('keydown', function(e) {
      const step = 5; // 5 second skip

      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        e.preventDefault();
        audio.currentTime = Math.min(audio.currentTime + step, audio.duration);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        e.preventDefault();
        audio.currentTime = Math.max(audio.currentTime - step, 0);
      }
    });

    // Update aria-valuenow on progress
    audio.addEventListener('timeupdate', function() {
      const progress = Math.round((audio.currentTime / audio.duration) * 100);
      progressBar.setAttribute('aria-valuenow', progress || 0);
    });

    // Store reference for pause all functionality
    audioPlayers.push({ container, audio });
  }

  /**
   * Pause all audio players except the specified one
   * @param {HTMLElement} exceptContainer - Container to exclude from pausing
   */
  function pauseAllExcept(exceptContainer) {
    audioPlayers.forEach(function(player) {
      if (player.container !== exceptContainer) {
        player.audio.pause();
      }
    });
  }

  /**
   * Initialize all audio overview blocks on the page
   */
  function initAllAudioPlayers() {
    const containers = document.querySelectorAll('.product-audio-overview');
    containers.forEach(function(container) {
      // Check if already initialized
      if (!container.dataset.initialized) {
        container.dataset.initialized = 'true';
        initAudioPlayer(container);
      }
    });
  }

  /**
   * Initialize a playlist audio player (.ap-player)
   * @param {HTMLElement} container - The ap-player container element
   */
  function initPlaylistAudioPlayer(container) {
    const audio = container.querySelector('.ap-audio');
    const playBtn = container.querySelector('.ap-play-btn');
    const progressBar = container.querySelector('.ap-progress-bar');
    const progressFill = container.querySelector('.ap-progress-fill');
    const progressThumb = container.querySelector('.ap-progress-thumb');
    const currentTimeEl = container.querySelector('.ap-current');
    const durationEl = container.querySelector('.ap-duration');

    if (!audio || !playBtn || !progressBar) {
      return;
    }

    // Load audio source from data attribute
    const audioSrc = container.dataset.audioSrc;
    if (audioSrc && !audio.src) {
      audio.src = audioSrc;
    }

    // Update duration when metadata loads
    audio.addEventListener('loadedmetadata', function() {
      if (durationEl) {
        durationEl.textContent = formatTime(audio.duration);
      }
    });

    // Handle duration display if metadata already loaded
    if (audio.duration && !isNaN(audio.duration) && durationEl) {
      durationEl.textContent = formatTime(audio.duration);
    }

    // Play/Pause toggle
    playBtn.addEventListener('click', function() {
      if (audio.paused) {
        // Pause all other audio players first
        pauseAllExcept(container);
        audio.play();
      } else {
        audio.pause();
      }
    });

    // Update UI when playing
    audio.addEventListener('play', function() {
      container.classList.add('is-playing');
      playBtn.setAttribute('aria-label', 'Pause audio');
    });

    // Update UI when paused
    audio.addEventListener('pause', function() {
      container.classList.remove('is-playing');
      playBtn.setAttribute('aria-label', 'Play audio');
    });

    // Update progress bar, thumb, and current time during playback
    audio.addEventListener('timeupdate', function() {
      const progress = (audio.currentTime / audio.duration) * 100;
      if (progressFill) {
        progressFill.style.width = `${progress}%`;
      }
      if (progressThumb) {
        progressThumb.style.left = `${progress}%`;
      }
      if (currentTimeEl) {
        currentTimeEl.textContent = formatTime(audio.currentTime);
      }
    });

    // Reset when audio ends
    audio.addEventListener('ended', function() {
      container.classList.remove('is-playing');
      playBtn.setAttribute('aria-label', 'Play audio');
      if (progressFill) {
        progressFill.style.width = '0%';
      }
      if (progressThumb) {
        progressThumb.style.left = '0%';
      }
      if (currentTimeEl) {
        currentTimeEl.textContent = '0:00';
      }
    });

    // Click to seek on progress bar
    progressBar.addEventListener('click', function(e) {
      const rect = progressBar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = clickX / width;
      const newTime = percentage * audio.duration;

      if (!isNaN(newTime) && isFinite(newTime)) {
        audio.currentTime = newTime;
      }
    });

    // Store reference for pause all functionality
    audioPlayers.push({ container, audio });
  }

  /**
   * Initialize all playlist audio players on the page
   */
  function initAllPlaylistPlayers() {
    const containers = document.querySelectorAll('.ap-player');
    containers.forEach(function(container) {
      // Check if already initialized
      if (!container.dataset.initialized) {
        container.dataset.initialized = 'true';
        initPlaylistAudioPlayer(container);
      }
    });
  }

  /**
   * Initialize all audio players (both types)
   */
  function initAll() {
    initAllAudioPlayers();
    initAllPlaylistPlayers();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-initialize when Shopify section is rendered (for theme editor)
  document.addEventListener('shopify:section:load', function(e) {
    // Audio Overview blocks
    const aoContainer = e.target.querySelector('.product-audio-overview');
    if (aoContainer && !aoContainer.dataset.initialized) {
      aoContainer.dataset.initialized = 'true';
      initAudioPlayer(aoContainer);
    }

    // Playlist players
    const apContainers = e.target.querySelectorAll('.ap-player');
    apContainers.forEach(function(container) {
      if (!container.dataset.initialized) {
        container.dataset.initialized = 'true';
        initPlaylistAudioPlayer(container);
      }
    });
  });

  // Handle block selection in theme editor
  document.addEventListener('shopify:block:select', function(e) {
    const aoContainer = e.target.closest('.product-audio-overview');
    if (aoContainer) {
      aoContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });

})();
